// Pure request-body builder for the "New picture book" wizard (Picture-Book
// Workflow plan, Task 3.1). The wizard page (`NewBook.tsx`) is orchestration
// only: it POSTs/PATCHes these bodies against existing endpoints in sequence.
// No `api` import here so this stays trivially unit-testable.

/** The picture-book outline template seeded by `shared/src/templateSeed.ts`. */
export const OUTLINE_TEMPLATE_ID = "text/picture-book-outline";

/** Default illustration route the wizard writes into the project defaults
 *  (plan decision P-9). The owner enables it with a real OpenAI key in Phase 5;
 *  until then illustration jobs fail with a typed VENDOR_NOT_CONFIGURED. */
export const ILLUSTRATION_ROUTE_ID = "openai/gpt-image-1-5";

/** Standard 32-page picture book is ~16 spreads. */
export const DEFAULT_PAGE_COUNT = 16;

export interface NewBookForm {
  title: string;
  premise: string;
  /** One `Name - description` per line. */
  characters: string;
  /** e.g. "3-5". */
  ageRange: string;
  /** Number of pages to ask the outline for; coerced to an integer >= 1. */
  pageCount: number | string;
  artStyle: string;
  /** Look-and-feel to avoid. Optional; empty -> negativeGuidance "". */
  avoid: string;
}

export interface NewBookProjectBody {
  name: string;
  description: string;
  styleGuide: string;
  sharedContext: string;
  negativeGuidance: string;
}

export interface NewBookProjectDefaultsBody {
  defaults: {
    image: { routeId: string; variantCount: number };
    text: { variantCount: number };
  };
}

export interface NewBookOutlineItemBody {
  externalId: string;
  title: string;
  brief: string;
}

export interface NewBookOutlineDeliverableBody {
  key: string;
  contentType: "text";
  templateId: string;
  instructions: string;
}

export interface NewBookRequests {
  project: NewBookProjectBody;
  projectDefaults: NewBookProjectDefaultsBody;
  outlineItem: NewBookOutlineItemBody;
  outlineDeliverable: NewBookOutlineDeliverableBody;
}

/** Coerce the form's `pageCount` (a number or a raw text-input string) to an
 *  integer >= 1, falling back to DEFAULT_PAGE_COUNT for anything non-numeric. */
export function normalizePageCount(raw: number | string): number {
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n >= 1 ? n : DEFAULT_PAGE_COUNT;
}

export function buildNewBookRequests(form: NewBookForm): NewBookRequests {
  const title = form.title.trim();
  const premise = form.premise.trim();
  const characters = form.characters.trim();
  const ageRange = form.ageRange.trim();
  const artStyle = form.artStyle.trim();
  const avoid = form.avoid.trim();
  const pageCount = normalizePageCount(form.pageCount);

  return {
    project: {
      name: title,
      description: "Picture book",
      styleGuide: `${artStyle}\nAudience age: ${ageRange}`,
      sharedContext: `Characters:\n${characters}`,
      negativeGuidance: avoid,
    },
    projectDefaults: {
      defaults: {
        image: { routeId: ILLUSTRATION_ROUTE_ID, variantCount: 1 },
        text: { variantCount: 1 },
      },
    },
    outlineItem: {
      externalId: "outline",
      title: "Outline",
      brief: premise,
    },
    outlineDeliverable: {
      key: "outline",
      contentType: "text",
      templateId: OUTLINE_TEMPLATE_ID,
      instructions: `Write exactly ${pageCount} pages. Age range: ${ageRange}.`,
    },
  };
}
